import React, { useCallback, useEffect, useState } from "react";
import Papa from "papaparse";
import AdminHeader from "./Header";
import "../../styles/Super.css";
import { useNotification } from "../../Toast/ToastProvider";
import { adminReqConfig, api, baseURL } from "../../include/api";
import { useNavigate, useParams } from "react-router-dom";
import { formatCustomerId, formatInvoiceId, formatMonthDateYear, formatNumber } from "../../include/function";
import { ChevronDown } from "react-feather";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ERROR, SUCCESS } from "../../include/constant";
import CustomerTransaction from "./CustomerTransaction";
import CustomerOrders from "./CustomerOrders";
import CustomerInvoice from "./CustomerInvoice";

const CustomerDetails = () => {
  const { id } = useParams();
  const toast = useNotification();
  const navigate = useNavigate();
  const [customerDetail, setCustomerDetail] = useState([]);
  const [customerSummary, setCustomerSummary] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [orderData, setOrderData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditingCreditLimit, setIsEditingCreditLimit] = useState(false);
  const [isEditingCreditDays, setIsEditingCreditDays] = useState(false);
  const [creditBal, setCreditBal] = useState("");
  const [creditLimit, setCreditLimit] = useState("");
  const [view, setView] = useState("transaction");

 const getCustomerDetail = useCallback(() => {
   setFetchLoading(true);
   api
     .get(`/customer/?id=${id}`, adminReqConfig())
     .then((response) => {
       if (response.status === 200) {
         const customer = response.data.data[0] || null;
         setCustomerDetail(customer);
       }
     })
     .catch((error) => {
       toast({ type: "error", message: "Failed to load customer detail." });
     })
     .finally(() => {
       setFetchLoading(false);
     });
 }, [id]);
  
 const getCustomerSummary = useCallback(() => {
   setFetchLoading(true);
   api
     .get(`/customer/?id=${id}&flag=getsummary`, adminReqConfig())
     .then((response) => {
       if (response.status === 200) {
         const customer = response.data.data || [];
         setCustomerSummary(customer);

       
       }
     })
     .catch((error) => {
     })
     .finally(() => {
       setFetchLoading(false);
     });
 }, [id]);

  const getOrder = () => {
    setFetchLoading(true);

    let url = `/super_sales_invoice/?custId=${id}`;
    api
      .get(url, adminReqConfig())
      .then((response) => {
        if (response.status === 200 && Array.isArray(response.data.data)) {
          const normalizedData = response.data.data.map((order) => ({
            ...order,
            orderItems: (order.orderItems || []).map((item) => ({
              ...item,
              paymentStatus: item.paymentStatus?.toLowerCase(),
              status: item.status?.toLowerCase(),
            })),
          }));
          setOrderData(normalizedData);
          setFilteredData(normalizedData);
        } else {
          setOrderData([]);
          setFilteredData([]);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch invoice:", error);
        setOrderData([]);
        setFilteredData([]);
      })
      .finally(() => setFetchLoading(false));
  };

  useEffect(() => {
    getCustomerDetail();
    getCustomerSummary();
    getOrder();
  }, []);

  const handleOrderDetailClick = (orderId) => {
    navigate(`/admin/invoice-details/${orderId}`);
  };

  //Search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredData(orderData); // show all invoices again
    } else {
      const filtered = orderData.filter(
        (order) =>
          order.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          order.orderItems.some((item) =>
            item.productName?.toLowerCase().includes(searchQuery.toLowerCase())
          )
      );
      setFilteredData(filtered);
      setCurrentPage(1);
    }
  }, [searchQuery, orderData]);

  const handleDownloadPDF = (invoiceId) => {
    // Find the specific invoice data
    const invoice = orderData.find((order) => order.id === invoiceId);
    
    if (!invoice) {
      toast({ type: "error", message: "Invoice data not found." });
      console.error("Invoice not found for ID:", invoiceId);
      return;
    }

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    // --- Title ---
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Invoice Details", 105, 15, { align: "center" });

    // --- Basic Info ---
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Invoice ID: ${formatInvoiceId(invoice)}`, 14, 25);
    // Use the 'date' field from your data structure, or createTimestamp if needed
    doc.text(
      `Date: ${invoice.createTimestamp ? formatMonthDateYear(invoice.createTimestamp) : "N/A"}`,
      150,
      25,
    );

    // --- Customer Info ---
    doc.setFont("helvetica", "bold");
    doc.text("Customer Details:", 14, 35);
    doc.setFont("helvetica", "normal");
    doc.text(`Name: ${customerDetail.name || "N/A"}`, 14, 40);
    // Assuming customerType might come from customerDetail state, otherwise use from invoice if available
    doc.text(`Customer Type: ${customerDetail.customerType || "N/A"}`, 14, 45);
    // Assuming paymentMode comes from the first item or order level if available in your structure
    doc.text(
      `Payment Mode: ${invoice.orderItems?.[0]?.paymentMode || "N/A"}`,
      14,
      50
    );

    // --- Billing Address (Assuming same as delivery for this view, adjust if separate field exists)
    doc.setFont("helvetica", "bold");
    doc.text("Billing Address:", 14, 60);
    doc.setFont("helvetica", "normal");
    const billingAddress = `${invoice.address || ""}, ${invoice.city || ""}, ${
      invoice.state || ""
    } - ${invoice.pin || ""}`.replace(/, \s*$/, "");
    doc.text(billingAddress, 14, 65, { maxWidth: 180 });

    // --- Delivery Address ---
    doc.setFont("helvetica", "bold");
    doc.text("Delivery Address:", 14, 75);
    doc.setFont("helvetica", "normal");
    const deliveryAddress = billingAddress; // Same as billing in this context, adjust if needed
    doc.text(deliveryAddress, 14, 80, { maxWidth: 180 });

    const tableBody = [];

    // --- Product Rows ---
    if (invoice.orderItems?.length > 0) {
      invoice.orderItems.forEach((item, i) => {
        const price = Number(item.price || 0);
        const qty = Number(item.qty || 0);
        const discount = Number(item.discountValue || 0);
        const productAmount = (price * qty).toFixed(2);
        const amount = (productAmount - discount).toFixed(2);

        tableBody.push([
          i + 1,
          item.productName || "N/A",
          qty,
          `${price.toFixed(2)}`,
          `${discount}%`,
          `${amount}`,
        ]);
      });
    } else {
      // If no items, add a placeholder row
      tableBody.push(["-", "No items found", "-", "-", "-", "-"]);
    }

    // --- Summary Rows ---
    // Assuming these fields exist in your invoice object based on handleDownloadCSV logic
    // Adjust field names if they differ in your actual API response structure
    const totalDiscount = Number(invoice.totalDiscount || 0);
const totalDeliveryCharge = Number(
  invoice.orderItems?.[0]?.deliveryCharge || 0
);    const totalAmount = Number(invoice.totalAmount || 0);

    tableBody.push(
      [
        { content: "Total Discount", colSpan: 5, styles: { halign: "right" } },
        { content: `-${totalDiscount.toFixed(2)}` },
      ],
      [
        {
          content: "Delivery Charges",
          colSpan: 5,
          styles: { halign: "right" },
        },
        { content: `${totalDeliveryCharge.toFixed(2)}` },
      ],
      [
        {
          content: "Grand Total",
          colSpan: 5,
          styles: { halign: "right", fontStyle: "bold" },
        },
        {
          content: `${totalAmount.toFixed(2)}`,
          styles: { fontStyle: "bold" },
        },
      ]
    );

    // --- Table Render ---
    autoTable(doc, {
      startY: 90,
      head: [["S.No", "Product", "Qty", "Rate", "Discount", "Amount"]],
      body: tableBody,
      theme: "grid",
      styles: {
        fontSize: 9,
        cellPadding: 2,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
    });

    // --- Footer ---
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFontSize(8);
    doc.text("Thank you for your business!", 105, pageHeight - 10, {
      align: "center",
    });

    // Save the PDF
    doc.save(`Invoice_${formatInvoiceId(invoice)}.pdf`);
  };

  useEffect(() => {
    if (customerDetail) {
      setCreditBal(customerDetail.creditBal || "");
      setCreditLimit(customerDetail.creditLimit || "");
    }
  }, [customerDetail]);

 

  const handleSaveCreditAndLimit = () => {
      const payload = {
          creditBal,
          creditLimit
        }
        setFetchLoading(true);
        api
          .put(
            `/customer/?id=${id}&flag=changeAdminLimit`,
            payload,
            adminReqConfig()
          )
          .then((response) => {
            if (response.status === 200) {
              toast({
                type: SUCCESS,
                message: "Credit details added successfully!",
              });
              
              getCustomerDetail();
            }
          })
          .catch(() => {
            toast({ type:ERROR, message: "Failed to update credit details!" });
          }).finally(() => {
            setFetchLoading(false);
          })
  }

  const clearSaveCreditAndLimit = () =>{
    setCreditBal("");
    setCreditLimit("");
  }

const cementSummary = customerSummary?.find(
  (item) => item.categoryName?.toLowerCase() === "cement"
);

const steelSummary = customerSummary?.find(
  (item) => item.categoryName?.toLowerCase() === "steel"
  );
  
    const getDaysCrossed = (timestamp) => {
      if (!timestamp) return 0;

      const orderDate = new Date(timestamp);
      const now = new Date();

      const diffTime = now.getTime() - orderDate.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      return diffDays >= 0 ? diffDays : 0;
    };


  return (
    <>
      <AdminHeader title={"CUSTOMER DETAILS"}>
        <div className="content-wrapper p-0">
          <section
            style={{
              backgroundColor: "#F1F4F9",
              padding: "30px",
              marginTop: "70px",
              marginBottom: "40px",
              minHeight: "90vh",
            }}
          >
            <div className="row g-4 ">
              {/* Customer Profile Card */}
              <div className="col-lg-4">
                <div className="bg-white rounded shadow-sm">
                  <div className="profile-card-top mb-3">
                    <div className="profile-card-top-img">
                      <img
                        src="/assets/images/img-profile.png"
                        alt="Customer"
                        className="w-100 h-100"
                      />
                    </div>
                  </div>
                  <div className="profile-card-top-text">
                    <h6 className="mb-1">{customerDetail.name}</h6>
                    <p className="text-muted mb-3">
                      {customerDetail.customerType || "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="col-lg-8">
                <div className="row g-4">
                  <div className="col-md-6">
                    <div
                      className="profile-totals-card shadow-sm"
                      onClick={() => setView("orders")}
                    >
                      <div className=" me-3">
                        <div className="profile-totals-card-img">
                          <img
                            src="/assets/images/tot-order.png"
                            style={{ height: "55px", width: "55px" }}
                          />
                        </div>
                      </div>
                      <div>
                        <p className="cust-detail-summary">Total Orders</p>
                        <h6 className="fs-4">{customerDetail.totalOrders}</h6>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="profile-totals-card shadow-sm">
                      <div className=" me-3">
                        <div className="profile-totals-card-img">
                          <img
                            src="/assets/images/tot-ton.png"
                            style={{ height: "40px", width: "40px" }}
                          />
                        </div>
                      </div>
                      <div>
                        <p className="cust-detail-summary">Total Tonnage</p>
                        <h6 className="fs-4">
                          {customerDetail.totalOrders || "N/A"}
                        </h6>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div
                      className="profile-totals-card shadow-sm"
                      onClick={() => setView("transaction")}
                    >
                      <div className=" me-3">
                        <div className="profile-totals-card-img">
                          <img
                            src="/assets/images/tot-tran.png"
                            style={{ height: "40px", width: "50px" }}
                          />
                        </div>
                      </div>
                      <div>
                        <p className="cust-detail-summary">Total Transaction</p>
                        <h6 className="fs-4">
                          {Number(customerDetail?.totalTransaction).toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                        </h6>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div
                      className="profile-totals-card shadow-sm"
                      onClick={() => setView("invoice")}
                    >
                      <div className=" me-3">
                        <div className="profile-totals-card-img">
                          <img
                            src="/assets/images/tot-inv.png"
                            style={{ height: "40px", width: "40px" }}
                          />
                        </div>
                      </div>
                      <div>
                        <p className="cust-detail-summary">Total Invoices</p>
                        <h6 className="fs-4">
                          {customerDetail.totalInvoiceOrders}
                        </h6>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content Section */}
            <div className="row g-4 mt-1">
              {/* Left Column - Customer Details & Latest Invoice */}
              <div className="col-lg-4">
                {/* Customer Details Card */}
                <div className="bg-white rounded shadow-sm p-16 mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">Customer Details</h6>
                    <span
                      style={{
                        backgroundColor: "#caeadc",
                        paddingLeft: "12px",
                        paddingRight: "12px",
                        textTransform: "capitalize",
                        color: "#3c9c73",
                        fontWeight: "500",
                        fontSize: "12px",
                        borderRadius: "15px",
                      }}
                    >
                      {customerDetail.status} User
                    </span>
                  </div>

                  <div className="profile-left-row-cus-information ">
                    <div className="d-flex gap-12 mb-8">
                      <span className="profile-left-row-span">Customer ID</span>
                      <p className="profile-left-row-p">
                        {formatCustomerId(customerDetail)}
                      </p>
                    </div>
                    <div className="d-flex gap-52 mb-8">
                      <span className="profile-left-row-span">Phone</span>
                      <p className="profile-left-row-p">
                        {customerDetail.phoneNumber || "N/A"}
                      </p>
                    </div>
                    <div className="d-flex gap-40 mb-8">
                      <span className="profile-left-row-span">Email ID</span>
                      <p className="profile-left-row-p">
                        {customerDetail.emailId || "N/A"}
                      </p>
                    </div>
                    <div className="d-flex gap-40">
                      <span className="profile-left-row-span">
                        Delivery
                        <br /> Address
                      </span>
                      <p
                        className="profile-left-row-p"
                        style={{ maxWidth: "210px" }}
                      >
                        {customerDetail.address1 || "N/A"}
                      </p>
                    </div>
                    <div className="text-end mt-18">
                      <button
                        className=""
                        style={{
                          color: "#2B95D3",
                          fontSize: "15px",
                          fontWeight: "500",
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          setIsModalOpen(true);
                        }}
                      >
                        View All
                      </button>
                    </div>
                  </div>

                  <div
                    style={{ display: "flex", gap: "10px", marginTop: "20px" }}
                  >
                    <button
                      style={{
                        color: "#fff",
                        backgroundColor: "#2b95d3",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: "700",
                        cursor: "pointer",
                        padding: "8px 16px",
                        minWidth: "130px",
                        height: "40px",
                        border: "none",
                      }}
                      fetchLoading={"loading..."}
                    >
                      Send Message
                    </button>
                  </div>
                </div>

                {/* Latest Invoice Card */}
                <div className="bg-white rounded shadow-sm p-16 mt-24">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">Latest Invoice</h6>
                  </div>
                  <p
                    style={{
                      fontSize: "12px",
                      fontWeight: "500",
                      color: "#7A869D",
                    }}
                  >
                    Total {orderData.length} Files
                  </p>

                  <div
                    style={{
                      maxHeight: "500px",
                      overflowY: "auto",
                      paddingRight: "5px",
                    }}
                  >
                    <div className="list-group mt-10">
                      {orderData?.map((invoice, index) => (
                        <div
                          key={index}
                          className="d-flex align-items-center gap-10 p-10 mb-5"
                          style={{
                            background: "#F8FAFC",
                            borderRadius: "8px",
                          }}
                        >
                          <div className="profile-cus-inv-img">
                            <img
                              src="/assets/images/tot-inv.png"
                              style={{ height: "20px", width: "20px" }}
                            />
                          </div>

                          <div className="flex-grow-1">
                            <button
                              onClick={() => handleOrderDetailClick(invoice.id)}
                              style={{
                                background: "none",
                                border: "none",
                                color: "black",
                                fontWeight: "500",
                                cursor: "pointer",
                                padding: 0,
                                fontSize: "13px",
                              }}
                              title="View Order Details"
                            >
                              Invoice ID {formatInvoiceId(invoice)}
                            </button>
                            <p
                              style={{
                                fontSize: "12px",
                                color: "#7A869D",
                                fontWeight: "500",
                                marginBottom: "0",
                              }}
                            >
                              {invoice.date}
                            </p>
                          </div>

                          <div className="dropdown">
                            <button
                              className="btn  p-0"
                              type="button"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                            >
                              <i
                                className="bi bi-three-dots-vertical"
                                style={{ color: "#6F7D95" }}
                              ></i>
                            </button>

                            <ul className="dropdown-menu">
                              <li>
                                <a
                                  className="dropdown-item"
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleDownloadPDF(invoice.id);
                                  }}
                                >
                                  Download Pdf
                                </a>
                              </li>
                            </ul>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Transaction History & Product Summaries */}
              <div className="col-lg-8 ">
                <div className="row g-4">
                  {/* Credit Balance Card */}
                  <div className="col-md-6">
                    <div className="profile-credit-limit-card shadow-sm">
                      <div>
                        <h6
                          style={{
                            fontWeight: "700",
                            fontSize: "18px",
                            color: "#2c3e50",
                            marginBottom: "18px",
                          }}
                        >
                          Credit Balance
                        </h6>
                        <div className="">
                          {/* Progress Bar */}
                          <div
                            style={{
                              flex: 1,
                              height: "8px",
                              backgroundColor: "#e0e0e0",
                              borderRadius: "4px",
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                width: `${Math.min(
                                  (Number(customerDetail?.currentCredit || 0) /
                                    Number(customerDetail?.creditBal || 1)) *
                                    100,
                                  100
                                )}%`,
                                height: "100%",
                                backgroundColor:
                                  Number(customerDetail?.currentCredit || 0) >
                                  Number(customerDetail?.creditBal || 0)
                                    ? "#D32B2E"
                                    : "#3498db",
                                borderRadius: "4px",
                              }}
                            ></div>
                          </div>
                        </div>
                        <div className="d-flex justify-content-between mt-2 mb-14">
                          <span
                            className={`${
                              Number(customerDetail?.currentCredit || 0) >
                              Number(customerDetail?.creditBal || 0)
                                ? "text-danger"
                                : "text-muted"
                            }`}
                          >
                            ₹{formatNumber(customerDetail?.currentCredit || 0)}{" "}
                            used
                          </span>
                          <span className="text-muted">
                            out of ₹
                            {formatNumber(customerDetail?.creditBal || 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Credit Limit (In Days) Card */}
                  <div className="col-md-6">
                    <div className="profile-credit-limit-card shadow-sm">
                      <div>
                        <h6
                          style={{
                            fontWeight: "700",
                            fontSize: "18px",
                            color: "#2c3e50",
                            marginBottom: "18px",
                          }}
                        >
                          Credit Limit (In Days)
                        </h6>{" "}
                        <div className="d-flex align-items-center gap-2">
                          {/* Progress Bar */}
                          <div
                            style={{
                              flex: 1,
                              height: "8px",
                              backgroundColor: "#e0e0e0",
                              borderRadius: "4px",
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                width: `${Math.min(
                                  (getDaysCrossed(customerDetail?.creditDate) /
                                    Number(customerDetail?.creditLimit || 1)) *
                                    100,
                                  100
                                )}%`,
                                height: "100%",
                                backgroundColor:
                                  getDaysCrossed(customerDetail?.creditDate) >
                                  Number(customerDetail?.creditLimit || 0)
                                    ? "#D32B2E"
                                    : "#3498db",
                                borderRadius: "4px",
                              }}
                            ></div>
                          </div>
                        </div>
                        <div className="d-flex justify-content-between mt-2 mb-14">
                          <span
                            className={`${
                              getDaysCrossed(customerDetail?.creditDate) >
                              Number(customerDetail?.creditLimit || 0)
                                ? "text-danger"
                                : "text-muted"
                            }`}
                          >
                            {getDaysCrossed(customerDetail?.creditDate)} days
                            crossed
                          </span>
                          <span className="text-muted">
                            out of {customerDetail?.creditLimit || 0} days
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer message: show if either balance or days are exceeded */}
                  {(() => {
                    const currentCredit = Number(customerDetail?.currentCredit) || 0;
                    const creditBal = Number(customerDetail?.creditBal) || 0;
                    const daysCrossed = getDaysCrossed(
                      customerDetail?.creditDate
                    );
                    const creditLimitDays =
                      Number(customerDetail?.creditLimit) || 10;
                    const isOverBalance = currentCredit > creditBal;
                    const isOverDays = daysCrossed > creditLimitDays;

                    if (isOverBalance || isOverDays) {
                      return (
                        <p
                          style={{
                            marginTop: "16px",
                            fontSize: "13px",
                            color: "#6E7C94",
                            fontWeight: "",
                          }}
                        >
                          Limit will reset after total outstanding is cleared.
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>
                <div className="row mt-10" style={{padding:'10px'}}>
                  {view === "transaction" && 
                    <CustomerTransaction customerId={id} />
                  }
                  {view === "orders" && <CustomerOrders customerId={id} />}
                  {view === "invoice" && <CustomerInvoice customerId={id} />}
                </div>
                {/* Product Summary Cards */}
                <div className="row g-4 mt-4">
                  {/* Total Cements */}
                  <div className="col-md-6">
                    <div className="bg-white rounded shadow-sm p-16">
                      <p className="mb-3 fs-5 text-dark fw-bold ">
                        {" "}
                        Total Cements
                      </p>
                      <div className="row p-16">
                        <div className="col-4">
                          <div className="text-dark fw-bold small ">
                            Quantity
                          </div>
                          <div className="fw-bold">
                            {formatNumber(
                              cementSummary?.totalQuantity || "N/A"
                            )}
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="text-dark fw-bold  small">
                            Tonnage
                          </div>
                          <div className="fw-bold">
                            {customerDetail.tonnage || "N/A"}
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="text-dark fw-bold  small">
                            Total Amount
                          </div>
                          <div className="fw-bold">
                            {formatNumber(cementSummary?.totalAmount || "N/A")}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Total Steels */}
                  <div className="col-md-6">
                    <div className="bg-white rounded shadow-sm p-16">
                      <p className="mb-3 fs-5 text-dark fw-bold ">
                        Total Steels
                      </p>
                      <div className="row p-16">
                        <div className="col-4">
                          <div className="text-dark fw-bold small">
                            Quantity
                          </div>
                          <div className="fw-bold">
                            {formatNumber(steelSummary?.totalQuantity || "N/A")}
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="text-dark fw-bold small">Tonnage</div>
                          <div className="fw-bold">
                            {customerDetail.tonnage || "N/A"}
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="text-dark fw-bold small">
                            Total Amount
                          </div>
                          <div className="fw-bold">
                            {formatNumber(steelSummary?.totalAmount || "N/A")}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {isModalOpen && (
                <CustomerDetailModal
                  customerDetail={customerDetail}
                  setIsModalOpen={setIsModalOpen}
                  isEditingCreditLimit={isEditingCreditLimit}
                  setIsEditingCreditLimit={setIsEditingCreditLimit}
                  isEditingCreditDays={isEditingCreditDays}
                  setIsEditingCreditDays={setIsEditingCreditDays}
                  creditBal={creditBal}
                  setCreditBal={setCreditBal}
                  creditLimit={creditLimit}
                  setCreditLimit={setCreditLimit}
                  handleSaveCreditAndLimit={handleSaveCreditAndLimit}
                  clearSaveCreditAndLimit={clearSaveCreditAndLimit}
                  formatCustomerId={formatCustomerId}
                />
              )}
            </div>
          </section>
        </div>
      </AdminHeader>
    </>
  );
};

export default CustomerDetails;


const CustomerDetailModal = ({
  customerDetail,
  setIsModalOpen,
  isEditingCreditLimit,
  isEditingCreditDays,
  setIsEditingCreditLimit,
  setIsEditingCreditDays,
  creditBal,
  setCreditBal,
  creditLimit,
  setCreditLimit,
  handleSaveCreditAndLimit,
  clearSaveCreditAndLimit,
  formatCustomerId,
}) => {
  if (!customerDetail) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: -25,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
      }}
      onClick={() => setIsModalOpen(false)}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "8px",
          padding: "24px",
          width: "68%",
          maxWidth: "95vw",
          maxHeight: "90vh",
          overflowY: "auto",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => setIsModalOpen(false)}
          style={{
            position: "absolute",
            top: "12px",
            right: "12px",
            width: "32px",
            height: "32px",
            background: "none",
            border: "3px solid #ddd",
            borderRadius: "50%",
            fontSize: "28px",
            cursor: "pointer",
            color: "#6E7C94",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: 0,
          }}
        >
          &times;
        </button>

        {/* Modal Header */}
        <h6
          style={{ marginBottom: "20px", fontWeight: "700", fontSize: "20px" }}
        >
          Customer Details{" "}
          <span
            style={{
              backgroundColor: "#caeadc",
              paddingLeft: "12px",
              paddingRight: "12px",
              textTransform: "capitalize",
              color: "#3c9c73",
              fontWeight: "500",
              fontSize: "15px",
              borderRadius: "15px",
              marginLeft: "20px",
            }}
          >
            {customerDetail.status} User
          </span>
        </h6>

        {/* Basic Info */}
        <div className="row gap-10 mt-24">
          <div className="col-md-2">
            <div className="mb-3">
              <p className="fs-6 fw-bold text-dark">Customer ID</p>
              <p>{formatCustomerId(customerDetail)}</p>
            </div>
          </div>
          <div className="col-md-2">
            <div className="mb-3">
              <p className="fs-6 fw-bold text-dark">Customer Name</p>
              <p>{customerDetail.name}</p>
            </div>
          </div>
          <div className="col-md-2">
            <div className="mb-3">
              <p className="fs-6 fw-bold text-dark">Customer Type</p>
              <p>
                {customerDetail.customerType
                  ? `${customerDetail.customerType} (${
                      customerDetail.customerType === "individual"
                        ? "B2C"
                        : "B2B"
                    })`
                  : "N/A"}
              </p>
            </div>
          </div>
          <div className="col-md-2">
            <div className="mb-3">
              <p className="fs-6 fw-bold text-dark">Created At</p>
              <p>{customerDetail.createdAt || "N/A"}</p>
            </div>
          </div>
          <div className="col-md-2">
            <div className="mb-3">
              <p className="fs-6 fw-bold text-dark">GST Number</p>
              <p>{customerDetail?.proof[0]?.gstNo}</p>
            </div>
          </div>
        </div>

        <div className="row mt-24 gap-10">
            <div className="col-md-2">
            <div className="mb-3">
              <p className="fs-6 fw-bold text-dark">Phone Number</p>
              <p>{customerDetail.phoneNumber}</p>
            </div>
          </div>
          <div className="col-md-2">
            <div className="mb-3">
              <p className="fs-6 fw-bold text-dark">Alternative Phone Number</p>
              <p>{customerDetail.alternativePhoneNumber || "N/A"}</p>
            </div>
          </div>
          <div className="col-md-2">
            <div className="mb-3">
              <p className="fs-6 fw-bold text-dark">Email ID</p>
              <p>{customerDetail.emailId}</p>
            </div>
          </div>
          <div className="col-md-2">
            <div className="mb-3">
              <p className="fs-6 fw-bold text-dark">Alternative Email ID</p>
              <p>{customerDetail.alternativeEmailId || "N/A"}</p>
            </div>
          </div>
          <div className="col-md-2">
            <div className="mb-3">
              <p className="fs-6 fw-bold text-dark">PinCode</p>
              <p>{customerDetail.pincode || "N/A"}</p>
            </div>
          </div>
        </div>

        {/* Credit Limit & Days Section */}
        {customerDetail.customerType !== "individual" && customerDetail.proofStatus === 'approved' && (
          <div className="row mt-24">
            {/* Credit Limit */}
            <div className="col-md-4">
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <p className="fs-6 fw-bold text-dark">Credit Limit</p>
                  {isEditingCreditLimit ? (
                    <div className="d-flex gap-5 ">
                      <button
                        onClick={() => {
                          if (clearSaveCreditAndLimit)
                            clearSaveCreditAndLimit();
                          setIsEditingCreditLimit(true);
                        }}
                        style={{
                          backgroundColor: "#e7f2ff",
                          border: "none",
                          borderRadius: "4px",
                          padding: "4px 12px",
                          fontSize: "14px",
                          cursor: "pointer",
                          color: "#2b95d3",
                          marginBottom: "5px",
                        }}
                      >
                        Clear
                      </button>
                      <button
                        onClick={() => {
                          if (handleSaveCreditAndLimit)
                            handleSaveCreditAndLimit();
                          setIsEditingCreditLimit(false);
                        }}
                        style={{
                          backgroundColor: "#2b95d3",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          padding: "4px 12px",
                          fontSize: "14px",
                          cursor: "pointer",
                          marginBottom: "5px",
                        }}
                      >
                        Save
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setIsEditingCreditLimit(true);
                        setCreditBal(customerDetail.creditBal || "");
                      }}
                      style={{
                        backgroundColor: "#e7f2ff",
                        border: "none",
                        borderRadius: "4px",
                        padding: "4px 12px",
                        fontSize: "14px",
                        cursor: "pointer",
                        color: "#2b95d3",
                        marginBottom: "5px",
                      }}
                    >
                      Edit
                    </button>
                  )}
                </div>

                {isEditingCreditLimit ? (
                  <input
                    type="text"
                    className="form-control"
                    value={creditBal}
                    onChange={(e) => setCreditBal(e.target.value)}
                    placeholder="Enter credit limit"
                    style={{ flex: 1 }}
                  />
                ) : (
                  <input
                    type="text"
                    className="form-control"
                    value={
                      customerDetail.creditBal
                        ? customerDetail.creditBal.toLocaleString()
                        : "N/A"
                    }
                    readOnly
                    style={{ backgroundColor: "#F6F8FD" }}
                  />
                )}
              </div>
            </div>

            {/* Credit Days */}
            <div className="col-md-4">
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <p className="fs-6 fw-bold text-dark">Credit Days</p>
                  {isEditingCreditDays ? (
                    <div className="d-flex gap-5">
                      <button
                        onClick={() => {
                          if (clearSaveCreditAndLimit)
                            clearSaveCreditAndLimit();
                          setIsEditingCreditDays(true);
                        }}
                        style={{
                          backgroundColor: "#e7f2ff",
                          border: "none",
                          borderRadius: "4px",
                          padding: "4px 12px",
                          fontSize: "14px",
                          cursor: "pointer",
                          color: "#2b95d3",
                          marginBottom: "5px",
                        }}
                      >
                        Clear
                      </button>
                      <button
                        onClick={() => {
                          if (handleSaveCreditAndLimit)
                            handleSaveCreditAndLimit();
                          setIsEditingCreditDays(false);
                        }}
                        style={{
                          backgroundColor: "#2b95d3",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          padding: "4px 12px",
                          fontSize: "14px",
                          cursor: "pointer",
                          marginBottom: "5px",
                        }}
                      >
                        Save
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setIsEditingCreditDays(true);
                        setCreditLimit(customerDetail.creditLimit || "");
                      }}
                      style={{
                        backgroundColor: "#e7f2ff",
                        border: "none",
                        borderRadius: "4px",
                        padding: "4px 12px",
                        fontSize: "14px",
                        cursor: "pointer",
                        color: "#2b95d3",
                        marginBottom: "5px",
                      }}
                    >
                      Edit
                    </button>
                  )}
                </div>

                {isEditingCreditDays ? (
                  <input
                    type="text"
                    className="form-control"
                    value={creditLimit}
                    onChange={(e) => setCreditLimit(e.target.value)}
                    placeholder="Enter credit days"
                    style={{ flex: 1 }}
                  />
                ) : (
                  <input
                    type="text"
                    className="form-control"
                    value={customerDetail.creditLimit || "N/A"}
                    readOnly
                    style={{ backgroundColor: "#F6F8FD" }}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {customerDetail.customerType !== "individual" && (
          <div className="row mt-24">
            <p className="fs-5 fw-bold text-dark mb-10 mt-24">
              Attached Documents
            </p>
            {customerDetail?.proof && customerDetail.proof.length > 0 ? (
              customerDetail.proof.map((doc, index) => (
                <div key={index} className="col-md-4 mb-10">
                  <div className="p-3  py-14 d-flex ">
                    <div className="me-16 document-pdf-background">
                      <img
                        src="/assets/images/doc-pdf.png"
                        alt="View"
                        style={{
                          width: "35px",
                          height: "35px",
                          objectFit: "contain",
                        }}
                      />
                    </div>
                    <div className="flex-grow-1">
                      <p className="fs-6 fw-bold mb-1 text-dark">
                        {doc.proofName || "N/A"}
                      </p>
                      <a
                        href={baseURL + "/" + doc.proofAttachment}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                          e.preventDefault();
                          window.open(
                            baseURL + "/" + doc.proofAttachment,
                            "_blank"
                          );
                        }}
                        className="text-decoration-none fs-6"
                        style={{ color: "#2592D2", fontWeight: "500" }}
                      >
                        View
                      </a>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12">
                <p className="text-muted fs-6">No documents attached.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};